import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useUser } from '@clerk/clerk-react'
import { updateUserProfile } from '../lib/api'
import { trackOnboardingCompleted, trackOnboardingStep, trackSignup } from '../lib/analytics'
import { useThemeColors } from '../hooks/useThemeColors'
import { useTheme } from '../contexts/ThemeContext'
import { useIsMobile } from '../hooks/useIsMobile'

const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

const VERTICAL_OPTIONS = [
  { id: 'smart-cameras', category: 'PHYSICAL SECURITY', label: 'Smart Cameras / Physical Security' },
  { id: 'robotics', category: 'ROBOTICS', label: 'Robotics' },
  { id: 'drones-uav', category: 'AERIAL', label: 'Drones / UAV' },
  { id: 'autonomous-vehicles', category: 'AUTOMOTIVE', label: 'Autonomous Vehicles' },
  { id: 'healthcare', category: 'HEALTHCARE', label: 'Healthcare / Medical Imaging' },
  { id: 'vss', category: 'VIDEO SEARCH', label: 'Video Search & Summarization' },
  { id: 'video-ad-tech', category: 'AD TECH', label: 'Video Advertising / Ad Tech' },
  { id: 'research', category: 'RESEARCH', label: 'Research / Academia' },
  { id: 'other', category: 'OTHER', label: 'Other / Custom' },
] as const

type Vertical = (typeof VERTICAL_OPTIONS)[number]['id']

const REPLACING_OPTIONS = [
  { id: 'custom-pipeline', category: 'CUSTOM', label: 'Custom CV pipeline (YOLO, SAM, etc.)' },
  { id: 'cloud-vlm', category: 'CLOUD', label: 'Cloud VLM (Gemini, Gemini Vision, etc.)' },
  { id: 'nvidia-vss', category: 'NVIDIA', label: 'NVIDIA VSS Blueprint' },
  { id: 'nothing-yet', category: 'FRESH START', label: 'Nothing yet — starting fresh' },
  { id: 'other', category: 'OTHER', label: 'Other' },
] as const

type Replacing = (typeof REPLACING_OPTIONS)[number]['id']

const STEPS = ['Account', 'Industry', 'Replacing', 'Plan', 'Start']

function trackStepView(step: number) {
  trackOnboardingStep({ step: step + 1, step_name: STEPS[step] ?? 'Unknown' })
}

function ProgressBar({ step }: { step: number }) {
  const c = useThemeColors()
  return (
    <div style={{ display: 'flex', gap: '8px', marginBottom: '40px', justifyContent: 'center' }}>
      {STEPS.map((label, i) => {
        const active = i === step
        const done = i < step
        return (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: active || done ? c.signal : c.carbon,
                color: active || done ? c.textPrimary : c.textMuted,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '11px',
                fontWeight: 600,
                flexShrink: 0,
                transition: 'background 200ms',
              }}
            >
              {done ? '✓' : i + 1}
            </div>
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '12px',
                color: active ? c.textPrimary : c.textMuted,
                display: 'none',
              }}
            >
              {label}
            </span>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  width: '32px',
                  height: '1px',
                  background: done ? c.signal : c.carbon,
                  transition: 'background 200ms',
                }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

function inputStyle(c: ReturnType<typeof useThemeColors>, focus?: boolean): React.CSSProperties {
  return {
    width: '100%',
    background: c.bgPrimary,
    border: `1px solid ${focus ? c.signal : c.border}`,
    borderRadius: '3px',
    padding: '13px 14px',
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
    color: c.textPrimary,
    outline: 'none',
    boxShadow: focus ? `0 0 0 3px rgba(32,88,240,0.15)` : 'none',
    transition: 'border-color 150ms, box-shadow 150ms',
  }
}

function TileGrid<T extends string>({
  options,
  selected,
  onSelect,
  singleColumn,
}: {
  options: ReadonlyArray<{ id: T; category: string; label: string }>
  selected: T | null
  onSelect: (id: T) => void
  singleColumn?: boolean
}) {
  const c = useThemeColors()
  return (
    <div
      data-testid="tile-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: singleColumn ? '1fr' : '1fr 1fr',
        gap: '12px',
        marginBottom: '32px',
      }}
    >
      {options.map(tile => {
        const isSelected = selected === tile.id
        return (
          <button
            key={tile.id}
            type="button"
            onClick={() => onSelect(tile.id)}
            style={{
              background: isSelected ? 'rgba(32,88,240,0.08)' : c.bgSecondary,
              border: `1px solid ${isSelected ? c.signal : c.border}`,
              borderRadius: '3px',
              padding: '20px',
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'border-color 150ms, background 150ms',
            }}
          >
            <div
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '11px',
                fontWeight: 500,
                letterSpacing: '0.08em',
                color: c.signal,
                textTransform: 'uppercase',
                marginBottom: '8px',
              }}
            >
              {tile.category}
            </div>
            <div
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '15px',
                fontWeight: 600,
                color: c.textPrimary,
              }}
            >
              {tile.label}
            </div>
          </button>
        )
      })}
    </div>
  )
}

function OnboardingWithAuth() {
  const navigate = useNavigate()
  const c = useThemeColors()
  const isMobile = useIsMobile()
  useTheme() // retained for potential future theme-aware logo (OWA)
  const { user } = useUser()
  const [step, setStep] = useState(0)
  const [name, setName] = useState(user?.fullName ?? user?.firstName ?? '')
  const [nameFocus, setNameFocus] = useState(false)
  const [vertical, setVertical] = useState<Vertical | null>(null)
  const [replacing, setReplacing] = useState<Replacing | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Fire signup event once — this page is only reached immediately after account creation
  useEffect(() => {
    trackSignup('clerk')
  }, [])

  useEffect(() => {
    trackStepView(step)
  }, [step])

  async function handleSubmit() {
    if (!name || !vertical || !replacing) return
    setSubmitting(true)
    try {
      await user?.update({
        firstName: name,
        unsafeMetadata: { vertical, replacing },
      })
    } catch {
      // non-fatal — proceed
    }
    try {
      await updateUserProfile({ vertical, replacing })
    } catch {
      // non-fatal
    }
    trackOnboardingCompleted(vertical ?? 'unknown', 'free')
    // Legacy Web/Pro/Max/MaxHD subscription plans removed 2026-08-07 — the
    // intended-plan redirect is gone; clear any stale legacy flags and go home.
    localStorage.removeItem('primate_intended_plan')
    localStorage.removeItem('primate-invited')

    void navigate('/')
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: c.bgPrimary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '64px 24px',
      }}
    >
      <div style={{ maxWidth: '640px', width: '100%' }}>
        <div className="flex justify-center mb-8">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <img src="/Darwin_v3_vinyl_transparent.png" alt="" style={{ height: 34, width: 'auto', objectFit: 'contain' }} />
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
              <span style={{ fontSize: 16, fontWeight: 300, color: '#E6E9F4', letterSpacing: '-0.025em' }}>Primate</span>
              <span style={{ fontSize: 16, fontWeight: 300, color: '#2058F0', letterSpacing: '-0.025em' }}>Intelligence</span>
            </div>
          </div>
        </div>

        <ProgressBar step={step} />

        {/* Step 0: Name */}
        {step === 0 && (
          <div>
            <h2
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '28px',
                letterSpacing: '-0.02em',
                color: c.textPrimary,
                marginBottom: '8px',
              }}
            >
              Welcome to Primate Intelligence
            </h2>
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '16px',
                color: c.textMuted,
                marginBottom: '32px',
              }}
            >
              Let's get your account set up.
            </p>

            <div style={{ marginBottom: '24px' }}>
              <label
                htmlFor="onboarding-name"
                style={{
                  display: 'block',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: c.textPrimary,
                  marginBottom: '6px',
                }}
              >
                Your name
              </label>
              <input
                id="onboarding-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                onFocus={() => setNameFocus(true)}
                onBlur={() => setNameFocus(false)}
                required
                style={inputStyle(c, nameFocus)}
                placeholder="Your name"
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                disabled={!name.trim()}
                onClick={() => setStep(1)}
                style={{
                  background: name.trim() ? c.signal : c.carbon,
                  color: name.trim() ? c.textPrimary : c.textMuted,
                  borderRadius: '2px',
                  height: '44px',
                  padding: '0 24px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: name.trim() ? 'pointer' : 'not-allowed',
                  transition: 'background 150ms',
                }}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Vertical */}
        {step === 1 && (
          <div>
            <h2
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '28px',
                letterSpacing: '-0.02em',
                color: c.textPrimary,
                marginBottom: '8px',
              }}
            >
              What industry are you in?
            </h2>
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '16px',
                color: c.textMuted,
                marginBottom: '24px',
              }}
            >
              Help us tailor your experience.
            </p>

            <TileGrid options={VERTICAL_OPTIONS} selected={vertical} onSelect={setVertical} singleColumn={isMobile} />

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
              <button
                onClick={() => setStep(0)}
                style={{
                  background: 'transparent',
                  color: c.textMuted,
                  borderRadius: '2px',
                  height: '44px',
                  padding: '0 20px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 500,
                  border: `1px solid ${c.border}`,
                  cursor: 'pointer',
                }}
              >
                Back
              </button>
              <button
                disabled={!vertical}
                onClick={() => setStep(2)}
                style={{
                  background: vertical ? c.signal : c.carbon,
                  color: vertical ? c.textPrimary : c.textMuted,
                  borderRadius: '2px',
                  height: '44px',
                  padding: '0 24px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: vertical ? 'pointer' : 'not-allowed',
                  transition: 'background 150ms',
                }}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Replacing */}
        {step === 2 && (
          <div>
            <h2
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '28px',
                letterSpacing: '-0.02em',
                color: c.textPrimary,
                marginBottom: '8px',
              }}
            >
              What are you replacing?
            </h2>
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '16px',
                color: c.textMuted,
                marginBottom: '24px',
              }}
            >
              What does Primate Intelligence replace in your stack?
            </p>

            <TileGrid options={REPLACING_OPTIONS} selected={replacing} onSelect={setReplacing} singleColumn={isMobile} />

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
              <button
                onClick={() => setStep(1)}
                style={{
                  background: 'transparent',
                  color: c.textMuted,
                  borderRadius: '2px',
                  height: '44px',
                  padding: '0 20px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 500,
                  border: `1px solid ${c.border}`,
                  cursor: 'pointer',
                }}
              >
                Back
              </button>
              <button
                disabled={!replacing}
                onClick={() => setStep(3)}
                style={{
                  background: replacing ? c.signal : c.carbon,
                  color: replacing ? c.textPrimary : c.textMuted,
                  borderRadius: '2px',
                  height: '44px',
                  padding: '0 24px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: replacing ? 'pointer' : 'not-allowed',
                  transition: 'background 150ms',
                }}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 3 & 4: Plan / Start */}
        {(step === 3 || step === 4) && (
          <div>
            <h2
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '28px',
                letterSpacing: '-0.02em',
                color: c.textPrimary,
                marginBottom: '8px',
              }}
            >
              You're all set, {name.split(' ')[0]}
            </h2>
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '16px',
                color: c.textMuted,
                marginBottom: '32px',
              }}
            >
              Your account is ready. Upload a video and ask a question — you'll have a result in under 15 seconds.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
              <button
                onClick={() => setStep(2)}
                style={{
                  background: 'transparent',
                  color: c.textMuted,
                  borderRadius: '2px',
                  height: '44px',
                  padding: '0 20px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 500,
                  border: `1px solid ${c.border}`,
                  cursor: 'pointer',
                }}
              >
                Back
              </button>
              <button
                disabled={submitting}
                onClick={() => void handleSubmit()}
                style={{
                  background: c.signal,
                  color: c.textPrimary,
                  borderRadius: '2px',
                  height: '44px',
                  padding: '0 24px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                  transition: 'background 150ms',
                }}
              >
                {submitting ? 'Setting up...' : 'Try Primate Vision →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function OnboardingBase() {
  const navigate = useNavigate()
  const c = useThemeColors()
  const isMobile = useIsMobile()
  useTheme() // retained for potential future theme-aware logo (OB)
  const [step, setStep] = useState(0)
  const [name, setName] = useState('')
  const [nameFocus, setNameFocus] = useState(false)
  const [vertical, setVertical] = useState<Vertical | null>(null)
  const [replacing, setReplacing] = useState<Replacing | null>(null)

  useEffect(() => {
    trackStepView(step)
  }, [step])

  function handleFinish() {
    void navigate('/')
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: c.bgPrimary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '64px 24px',
      }}
    >
      <div style={{ maxWidth: '640px', width: '100%' }}>
        <div className="flex justify-center mb-8">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <img src="/Darwin_v3_vinyl_transparent.png" alt="" style={{ height: 34, width: 'auto', objectFit: 'contain' }} />
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
              <span style={{ fontSize: 16, fontWeight: 300, color: '#E6E9F4', letterSpacing: '-0.025em' }}>Primate</span>
              <span style={{ fontSize: 16, fontWeight: 300, color: '#2058F0', letterSpacing: '-0.025em' }}>Intelligence</span>
            </div>
          </div>
        </div>

        <ProgressBar step={step} />

        {step === 0 && (
          <div>
            <h2
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '28px',
                letterSpacing: '-0.02em',
                color: c.textPrimary,
                marginBottom: '8px',
              }}
            >
              Welcome to Primate Intelligence
            </h2>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', color: c.textMuted, marginBottom: '32px' }}>
              Let's get your account set up.
            </p>
            <div style={{ marginBottom: '24px' }}>
              <label
                htmlFor="ob-name"
                style={{
                  display: 'block',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: c.textPrimary,
                  marginBottom: '6px',
                }}
              >
                Your name
              </label>
              <input
                id="ob-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                onFocus={() => setNameFocus(true)}
                onBlur={() => setNameFocus(false)}
                style={inputStyle(c, nameFocus)}
                placeholder="Your name"
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                disabled={!name.trim()}
                onClick={() => setStep(1)}
                style={{
                  background: name.trim() ? c.signal : c.carbon,
                  color: name.trim() ? c.textPrimary : c.textMuted,
                  borderRadius: '2px',
                  height: '44px',
                  padding: '0 24px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: name.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h2
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '28px',
                letterSpacing: '-0.02em',
                color: c.textPrimary,
                marginBottom: '24px',
              }}
            >
              What industry are you in?
            </h2>
            <TileGrid options={VERTICAL_OPTIONS} selected={vertical} onSelect={setVertical} singleColumn={isMobile} />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
              <button
                onClick={() => setStep(0)}
                style={{
                  background: 'transparent',
                  color: c.textMuted,
                  borderRadius: '2px',
                  height: '44px',
                  padding: '0 20px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 500,
                  border: `1px solid ${c.border}`,
                  cursor: 'pointer',
                }}
              >
                Back
              </button>
              <button
                disabled={!vertical}
                onClick={() => setStep(2)}
                style={{
                  background: vertical ? c.signal : c.carbon,
                  color: vertical ? c.textPrimary : c.textMuted,
                  borderRadius: '2px',
                  height: '44px',
                  padding: '0 24px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: vertical ? 'pointer' : 'not-allowed',
                }}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '28px',
                letterSpacing: '-0.02em',
                color: c.textPrimary,
                marginBottom: '24px',
              }}
            >
              What are you replacing?
            </h2>
            <TileGrid options={REPLACING_OPTIONS} selected={replacing} onSelect={setReplacing} singleColumn={isMobile} />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
              <button
                onClick={() => setStep(1)}
                style={{
                  background: 'transparent',
                  color: c.textMuted,
                  borderRadius: '2px',
                  height: '44px',
                  padding: '0 20px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 500,
                  border: `1px solid ${c.border}`,
                  cursor: 'pointer',
                }}
              >
                Back
              </button>
              <button
                disabled={!replacing}
                onClick={handleFinish}
                style={{
                  background: replacing ? c.signal : c.carbon,
                  color: replacing ? c.textPrimary : c.textMuted,
                  borderRadius: '2px',
                  height: '44px',
                  padding: '0 24px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: replacing ? 'pointer' : 'not-allowed',
                }}
              >
                Get Started
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function Onboarding() {
  if (clerkKey) return <OnboardingWithAuth />
  return <OnboardingBase />
}
